import React, { useContext, useEffect, useState, MouseEvent } from 'react';
import { Icon, Link, TextLarge, TextRegular, UtilsBlock } from '@hz-design-system/web-ui';
import { getFormattedNumber } from '@ecg-marktplaats/aurora-js-formatters';
import { Trans } from '@ecg-marktplaats/js-react-i18n';

import { trackGAEvent } from '../../../../utils/gaTracking';
import { GA_PARAMETER, GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import createCustomDimension from '../../../../utils/createCustomDimension';
import TextSkeleton from '../../../../components/Skeleton/TextSkeleton';
import { INTERNAL_REVIEWS_SYSTEM } from '../../../../../../common/constants';

import { TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

import Classes from './SellerInfoSmall.scss';

type TSellerReviewsProps = {
  seller: TListingSellerInfo;
  profile: TSellerProfileClient;
};

let SellerReviewDialog;

const getDialogProps = ({
  handleClose,
  profile,
  name,
  internalReviewsUrl,
}: {
  handleClose: () => void;
  profile: TSellerProfileClient;
  name: string;
  internalReviewsUrl: string;
}) => ({
  onClose: handleClose,
  reviews: profile.reviews ?? [],
  sellerName: name,
  internalReviewsUrl: internalReviewsUrl,
});

const getClickTrackingProps = ({
  reviewSystem = '',
  averageScore,
}: {
  reviewSystem: string | undefined;
  averageScore: number | undefined;
}): Parameters<typeof trackGAEvent> => [
  GA_TRACK_EVENTS_ACTIONS.SELLER_REVIEW_CLICK,
  reviewSystem,
  { ...createCustomDimension({ dimension: GA_PARAMETER.REVIEW_AVERAGE_SCORE, payload: averageScore }) },
];

const SellerReviews = ({ seller, profile }: TSellerReviewsProps) => {
  const { links: { seller: sellerLinks = {} } = {}, hasLicensePlate } = useContext(EnvironmentContext);
  const { name } = seller;
  const [isSellerReviewDialogLoaded, setSellerReviewDialogLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const isProfileLoaded = Object.keys(profile).length !== 0;

  const firstReviewSystemData = profile.reviews?.[0] ?? {};
  const { numberOfReviews, reviewSystem, averageScore } = firstReviewSystemData;

  const minScore = 0;
  const maxScore = 5;
  const isRatingValid = !!averageScore && averageScore >= minScore && averageScore <= maxScore;
  const withReviewAttribution = reviewSystem === 'GOOGLE_REVIEWS';

  useEffect(() => {
    if (numberOfReviews || averageScore) {
      const isInternalSource = reviewSystem === INTERNAL_REVIEWS_SYSTEM;

      const gaEvent = isInternalSource
        ? GA_TRACK_EVENTS_ACTIONS.SellerRatingsAvailable
        : GA_TRACK_EVENTS_ACTIONS.SellerExternalAvailable;

      trackGAEvent(gaEvent, isInternalSource ? '' : reviewSystem, {
        ...createCustomDimension({
          dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
          payload: hasLicensePlate,
        }),
      });
    }
  }, [numberOfReviews, averageScore, hasLicensePlate, reviewSystem]);

  const loadSellerReviewDialog = async () => {
    if (!isSellerReviewDialogLoaded) {
      try {
        SellerReviewDialog = (
          await import(
            /* webpackChunkName: "SellerReviewsDialog" */ '../../../../components/SellerReviewsDialog/SellerReviewsDialog'
          )
        ).default;
        setSellerReviewDialogLoaded(true);
      } catch {}
    }
  };

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const trackingProps = getClickTrackingProps({ reviewSystem, averageScore });
    trackGAEvent(...trackingProps);

    await loadSellerReviewDialog();
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const props = getDialogProps({
    handleClose,
    profile,
    name,
    internalReviewsUrl: sellerLinks.reviews,
  });

  return (
    <div>
      {!isProfileLoaded && <TextSkeleton withAnimation={true} />}
      {isProfileLoaded && !numberOfReviews && (
        <>
          <Icon className={Classes.reviewIcon} name="review" color="text-brand-primary" />
          <Trans i18nKey="sellerReviews.noReviews" className={Classes.noReviewsPlaceholder} tagName="span" />
        </>
      )}
      {Boolean(numberOfReviews) && (
        <>
          <Icon className={Classes.reviewIcon} name="saved" color="text-brand-primary" />
          <TextLarge strong>{isRatingValid && `${averageScore.toFixed(1)} · `}</TextLarge>
          <Link href={sellerLinks.reviews} onClick={handleClick}>
            <Trans
              tagName="span"
              i18nKey="sellerInfo.review"
              count={numberOfReviews}
              formattedCount={getFormattedNumber(numberOfReviews)}
            />
          </Link>
          {withReviewAttribution && (
            <UtilsBlock as="span" marginTop="xxxs" className={Classes.reviewAttribution}>
              <Icon name="info" color="text-secondary" className="u-marginRightXxxxs" />
              <TextRegular color="text-secondary">Google Maps</TextRegular>
            </UtilsBlock>
          )}
        </>
      )}
      {isSellerReviewDialogLoaded && isOpen && <SellerReviewDialog {...props} />}
    </div>
  );
};

export default SellerReviews;
